import _ from 'underscore';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { AdsStudioSpinner } from '../ads-studio-spinner';
import { MultiSelectionGrid } from './multi-selection-grid';
import { getSectionTilesGridStyle } from './styles/section-tiles-grid-style';
import { TilesGrid } from './tiles-grid';
import { TilesGridLoadingCell } from './grid-cells';
import { withAdsStudioStyles } from '../../theme/with-ads-studio-styles';

const SectionTilesGridInternal = ({
  sectionData, onRenderCell, loadNextPage, multiSelectionEnabled, onSelectionChange, onRenderLoadingCell, classes, ...otherProps
}) => {
  const wrappedSectionData = useMemo(() => sectionData.map(section => ({
    onRenderCell: (params) => {
      const { sectionName } = section || {};
      return onRenderCell({ ...params, sectionName });
    },
    // to support selectoin on grid
    onSelectionChange: onSelectionChange ?
      (params) => {
        const { sectionName } = section || {};
        onSelectionChange({ ...params, sectionName });
      } : undefined,
    onRenderLoadingCell: () => {
      const { items, totalItemCount } = section || {};
      if (onRenderLoadingCell) {
        return onRenderLoadingCell({ items, totalItemCount });
      }
      if (items.length > 0 && items.length < totalItemCount) {
        return <TilesGridLoadingCell onLoading={loadNextPage} key={items.length} ><div className="section-loading"><AdsStudioSpinner useCustomSize /></div></TilesGridLoadingCell>;
      }
      return null;
    },
    ...section,
  })), [loadNextPage, onRenderCell, onRenderLoadingCell, onSelectionChange, sectionData]);

  // don't show section title if only one section has data
  const countOfSectionsWithData =
    _.filter(sectionData, sec => sec.totalItemCount > 0).length;

  const GridComponent = multiSelectionEnabled ? MultiSelectionGrid : TilesGrid;

  return (
    <div className={classnames(classes.sectionTilesGridContainer, classes.gridConainerClass)}>
      {wrappedSectionData.map(({
        sectionName,
        sectionTitle,
        sectionClassName,
        items,
        config,
        totalItemCount,
        ...rest
      }) => (
        (items && items.length >= 0) ?
          <div
            key={sectionName}
            className={classnames(sectionClassName, 'tiles-grid-section')}
          >
            {countOfSectionsWithData > 1 && items.length > 0 ? <div className="section-title" role="heading" aria-level="2">{sectionTitle}</div> : null}
            {
              items.length > 0 &&
              <GridComponent
                items={items}
                {...rest}
                {...otherProps}
                config={config}
              />
            }
            {
              rest.onRenderLoadingCell()
            }
          </div>
       : null
      ))}
    </div>
  );
};

SectionTilesGridInternal.propTypes = {
  sectionData: PropTypes.arrayOf(PropTypes.shape({
    sectionName: PropTypes.string,
    sectionTitle: PropTypes.string,
    sectionClassName: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({})),
    selectedItems: PropTypes.arrayOf(PropTypes.shape({})), // to support multiple selection
    totalItemCount: PropTypes.number, // total item count, including not loaded items
    config: PropTypes.shape({
      aspectRatio: PropTypes.number,
      maxColumnWidth: PropTypes.number,
      minColumnWidth: PropTypes.number,
      rowHeight: PropTypes.number,
      preferredCellsPerPage: PropTypes.number,
    }),
  })).isRequired,
  onRenderCell: PropTypes.func.isRequired,
  loadNextPage: PropTypes.func,

  multiSelectionEnabled: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  onRenderLoadingCell: PropTypes.func,

  classes: PropTypes.shape({
    sectionTilesGridContainer: PropTypes.string,
    gridConainerClass: PropTypes.string,
  }).isRequired,
};

SectionTilesGridInternal.defaultProps = {
  multiSelectionEnabled: false,
  onSelectionChange: undefined,
  loadNextPage: _.noop,
  onRenderLoadingCell: undefined,
};

export const SectionTilesGrid = withAdsStudioStyles(SectionTilesGridInternal, getSectionTilesGridStyle);
